/**
 * O Resumo da OS — o que a folha impressa dizia, vivo (onda 280).
 *
 * «Temos que ter uma visão geral da OS (Amostras, programação, etc)
 *  diretamente no sistema. Principalmente para que os laboratoristas tenham
 *  a visão geral do escopo. Hoje essa informação fica concentrada no PDF da
 *  OS, que na verdade é uma mera formalidade, pois a ideia é extinguir
 *  folhas impressas no laboratório, mantendo apenas etiquetas e uso do
 *  celular/pc para controle e execução dos ensaios.» — Humberto, 06/10/2026.
 *
 * Desenho aprovado no quadro «OS · Resumo e Recebimentos»
 * (`claude/000-ideia-resumo-da-os.md`): as amostras em GRADE e a faixa de
 * situação no lugar do cartão grande — decididos por ele em 06/10.
 *
 * A aba é o PDF da OS (`MOD.OS.LAB.R01`) com a situação de cada coisa:
 *
 *   · a faixa de situação — as três frentes e o prazo numa linha só; a
 *     explicação («as três andam separadas») mora no ⓘ;
 *   · Escopo e andamento — o quadro 1 do PDF («Serviços contratados»): por
 *     método, quanto foi aprovado, está na bancada, programado, marcado em
 *     amostra por chegar, e quanto ainda cabe;
 *   · Amostras e ensaios — o quadro 2: a grade amostras × métodos, só
 *     leitura, com o selo único do ensaio (267) na célula; clicar na
 *     amostra abre a ficha à direita (no celular, por baixo), com a data, a
 *     máquina, quem e a instrução de cada ensaio;
 *   · Instruções de execução — o quadro 3, em destaque;
 *   · Recebimentos — os lotes que chegaram para esta OS e o que ainda falta
 *     chegar;
 *   · Dados da OS — o cartão de sempre, que quem chama entrega pronto.
 *
 * Nenhuma conta nova mora aqui. A situação vem do ensaio (`status_ensaio`),
 * o saldo vem da `vw_os_escopo_por_metodo` (pelo `saldoDa` da faixa do
 * escopo), a agenda vem da mesma visão da Fila. A tela só arruma.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { data, dataHora, faixaProfundidade, plural, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { Selo, SeloDoEnsaio, SITUACAO_DO_ENSAIO, Vazio } from '../ui';
import type { Tom } from '../ui';
import {
  DIZ_STATUS, TITULO_STATUS,
  type AmostraPrevista, type CampoStatus, type Os,
} from './OsForms';
import { linhaDoServidor, saldoDa, type EscopoDoServidor } from './FaixaDoEscopo';
import { TOM_DA_AMOSTRA, type AmostraDoQuadro } from './QuadroDeAmostras';
import { numerosDaOs, prazoEmPalavras, rotaDaAba } from './OsMoldura';
import { AvisoSemOs } from './VincularRecebimento';
import { NomeDaAmostra, nomeDaAmostra, type AmostraComNome } from '../NomeDaAmostra';

/* ── O que vem da API ─────────────────────────────────────────────── */

/** `GET /os/:id/resumo` (280). */
export interface MetodoDoResumo {
  id: string; codigo: string; nome: string;
  escopo: string | null; escopo_ordem: number | null; na_os: boolean;
}
export interface EnsaioDoResumo {
  id: string; amostra_id: string; codigo: string; nome: string;
  escopo: string | null; status: string;
  instrucao: string | null; motivo_devolucao: string | null;
  executado_por_nome: string | null; atrasado: boolean | null;
  inicio_previsto: string | null; fim_previsto: string | null;
  equipamento_codigo: string | null; frente_nome: string | null;
  inicio_real: string | null;
}
export interface MarcaDoResumo {
  prevista_id: string; codigo: string; nome: string; instrucao: string | null;
}
interface ResumoDaApi {
  metodos: MetodoDoResumo[]; ensaios: EnsaioDoResumo[]; marcas: MarcaDoResumo[];
}

/** A amostra recebida, com o que a `vw_amostra` diz além do quadro. */
export interface AmostraDoResumo extends AmostraDoQuadro {
  lote_recebimento_id?: string | null;
  lote_codigo?: string | null;
  datahora_recebimento?: string | null;
  local_nome?: string | null;
  onde_esta?: string | null;
}

/** Um lote de `GET /recebimento?os=` (a `vw_lote_recebimento`). */
interface LoteDoResumo {
  id: string;
  codigo: string;
  decisao: string;
  status: string;
  datahora_recebimento: string;
  responsavel_entrega_nome: string | null;
  responsavel_entrega_empresa?: string | null;
  responsavel_recebimento: string | null;
  ressalvas: string | null;
  amostras: string | number;
  aprovado: boolean;
  aprovado_por_nome: string | null;
  aprovado_em: string | null;
  aprovacao_dispensada: boolean | null;
  pdf_revisao: number | null;
  /** Sem arquivo, não há PDF: a revisão 0 é a de antes da 1ª emissão. */
  pdf_arquivo_id: string | null;
}

/* ── Os tons ──────────────────────────────────────────────────────── */

/* As três frentes (moravam na ficha da OS até a 279, no cartão grande). */
const TOM_TECNICO: Record<string, Tom> = {
  rascunho: 'neutro', aguardando_aprovacao: 'neutro',
  aguardando_programacao: 'neutro', programada: 'andamento',
  aguardando_amostras: 'atencao', amostras_recebidas: 'andamento',
  ensaios_em_andamento: 'andamento', aguardando_dados: 'atencao',
  em_processamento: 'andamento', com_pendencia: 'critico',
  concluida: 'ok', suspensa: 'atencao', cancelada: 'neutro',
};
const TOM_RELATORIO: Record<string, Tom> = {
  nao_iniciado: 'neutro', em_elaboracao: 'andamento',
  em_verificacao: 'andamento', com_pendencia: 'critico',
  aprovado: 'ok', emitido: 'ok', disponibilizado: 'ok',
};
const TOM_MEDICAO: Record<string, Tom> = {
  nao_iniciada: 'neutro', em_medicao: 'andamento',
  aguardando_conferencia: 'atencao', aguardando_aprovacao: 'atencao',
  aprovada: 'ok', emitida: 'ok',
};
const TOM_POR_CAMPO: Record<CampoStatus, Record<string, Tom>> = {
  tecnico: TOM_TECNICO, relatorio: TOM_RELATORIO, medicao: TOM_MEDICAO,
};

/* A decisão do lote — o mesmo tom da ficha do recebimento (258). */
const TOM_DA_DECISAO: Record<string, Tom> = {
  aceite: 'ok', aceite_com_ressalva: 'atencao', rejeicao: 'critico',
};

/*
 * Qual ensaio a célula mostra quando a amostra tem mais de um do mesmo
 * método (uma repetição, por exemplo): o que mais pede alguém. Devolvido
 * antes de tudo; aprovado só se todos estiverem aprovados.
 */
const PRIORIDADE = [
  'devolvido_para_correcao', 'aguardando_verificacao', 'pausado', 'em_execucao',
  'dados_completos', 'em_processamento', 'programado', 'aprovado',
];
/** Na bancada: começou e ainda não foi aprovado. */
const NA_BANCADA = new Set([
  'em_execucao', 'pausado', 'dados_completos', 'em_processamento',
  'aguardando_verificacao', 'devolvido_para_correcao',
]);

/* ── As linhas da grade ───────────────────────────────────────────── */

type Recorte = 'todas' | 'a_fazer' | 'sem_ensaio' | 'por_chegar';

export interface LinhaDoResumo {
  chave: string;
  tipo: 'recebida' | 'por_chegar';
  /** Código da amostra; na prevista, a identificação prevista. */
  titulo: string;
  ident: string | null;
  furo: string;
  prof: string;
  tipoAmostra: string | null;
  ordem: number | null;
  amostra?: AmostraDoResumo;
  prevista?: AmostraPrevista;
  ensaios: EnsaioDoResumo[];
  marcas: MarcaDoResumo[];
}

const semAcento = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[\s\-_/.]+/g, '');

/** Furo em ordem natural (SP-2 antes de SP-10); sem furo vai para o fim. */
const ordemDoFuro = (x: string, y: string) =>
  x === y ? 0 : !x ? 1 : !y ? -1
    : x.localeCompare(y, 'pt-BR', { numeric: true, sensitivity: 'base' });

export function montarLinhas(
  recebidas: AmostraDoResumo[], previstas: AmostraPrevista[],
  ensaios: EnsaioDoResumo[], marcas: MarcaDoResumo[],
): LinhaDoResumo[] {
  const ordemDe = new Map<string, number>();
  for (const p of previstas) if (p.amostra_id) ordemDe.set(p.amostra_id, p.ordem);
  const ensaiosDe = new Map<string, EnsaioDoResumo[]>();
  for (const e of ensaios) {
    const l = ensaiosDe.get(e.amostra_id) ?? [];
    l.push(e);
    ensaiosDe.set(e.amostra_id, l);
  }
  const marcasDe = new Map<string, MarcaDoResumo[]>();
  for (const m of marcas) {
    const l = marcasDe.get(m.prevista_id) ?? [];
    l.push(m);
    marcasDe.set(m.prevista_id, l);
  }
  const linhas: LinhaDoResumo[] = [
    ...recebidas.map((a): LinhaDoResumo => ({
      chave: `a:${a.id}`, tipo: 'recebida', titulo: a.codigo,
      ident: a.identificacao_recebida, furo: a.ponto_investigacao?.trim() || '',
      prof: faixaProfundidade(a.profundidade_inicial, a.profundidade_final),
      tipoAmostra: a.tipo_amostra, ordem: ordemDe.get(a.id) ?? null,
      amostra: a, ensaios: ensaiosDe.get(a.id) ?? [], marcas: [],
    })),
    ...previstas.filter((p) => !p.atendida).map((p): LinhaDoResumo => ({
      chave: `p:${p.id}`, tipo: 'por_chegar',
      titulo: p.identificacao_prevista ?? 'Amostra prevista',
      ident: null, furo: p.origem_prevista?.trim() || '',
      prof: faixaProfundidade(p.profundidade_inicial, p.profundidade_final),
      tipoAmostra: p.tipo_amostra ?? null, ordem: p.ordem ?? null,
      prevista: p, ensaios: [], marcas: marcasDe.get(p.id) ?? [],
    })),
  ];
  /* A ordem da OS: a da previsão; o que chegou sem previsão vai para o fim,
     pelo código — a mesma da aba Amostras. */
  return linhas.sort((x, y) => {
    if (x.ordem !== null && y.ordem !== null) return x.ordem - y.ordem;
    if (x.ordem !== null) return -1;
    if (y.ordem !== null) return 1;
    return x.titulo.localeCompare(y.titulo, 'pt-BR', { numeric: true });
  });
}

const temEnsaio = (l: LinhaDoResumo) => l.ensaios.length > 0 || l.marcas.length > 0;
const aFazer = (l: LinhaDoResumo) =>
  l.tipo === 'recebida' && l.ensaios.some((e) => e.status !== 'aprovado');

const FILTRO: Record<Recorte, (l: LinhaDoResumo) => boolean> = {
  todas: () => true,
  a_fazer: aFazer,
  sem_ensaio: (l) => !temEnsaio(l),
  por_chegar: (l) => l.tipo === 'por_chegar',
};
const NOME_DO_RECORTE: Record<Recorte, string> = {
  todas: 'Todas', a_fazer: 'A fazer', sem_ensaio: 'Sem ensaio', por_chegar: 'Por chegar',
};

/** A célula de uma linha num método. */
interface Celula {
  classe: string; simbolo: string; titulo: string; instrucao: boolean;
}

export function celulaDe(l: LinhaDoResumo, codigo: string): Celula {
  if (l.tipo === 'por_chegar') {
    const m = l.marcas.filter((x) => x.codigo === codigo);
    if (!m.length) return { classe: 'nada', simbolo: '·', titulo: `${codigo}: não pedido`, instrucao: false };
    return {
      classe: 'por-chegar', simbolo: '○',
      titulo: `${codigo}: marcado — nasce programado quando a amostra chegar`,
      instrucao: m.some((x) => (x.instrucao ?? '').trim() !== ''),
    };
  }
  const es = l.ensaios.filter((e) => e.codigo === codigo);
  if (!es.length) return { classe: 'nada', simbolo: '·', titulo: `${codigo}: não pedido`, instrucao: false };
  const e = [...es].sort((a, b) =>
    PRIORIDADE.indexOf(a.status) - PRIORIDADE.indexOf(b.status))[0];
  const s = SITUACAO_DO_ENSAIO[e.status];
  return {
    classe: s?.tom ?? 'neutro',
    simbolo: s?.simbolo ?? '•',
    titulo: `${codigo}: ${es.map((x) => SITUACAO_DO_ENSAIO[x.status]?.texto ?? x.status).join(', ')}`,
    instrucao: es.some((x) => (x.instrucao ?? '').trim() !== ''),
  };
}

/* ── O componente ─────────────────────────────────────────────────── */

export function ResumoDaOs({
  os, previstas, recebidas, dados, aviso, aoPrevisao, aoMudar, etiquetaDe,
}: {
  os: Os;
  previstas: AmostraPrevista[];
  recebidas: AmostraDoResumo[];
  /** O cartão «Dados da OS», pronto (os formulários moram na ficha). */
  dados: ReactNode;
  /** Avisos que a ficha já calcula (cancelada, o que não entrou). */
  aviso?: ReactNode;
  /** Abre a janela «Previsão de chegada» da ficha. */
  aoPrevisao?: () => void;
  /** Recarrega a ficha (281: depois de vincular um recebimento sem OS). */
  aoMudar?: () => void | Promise<void>;
  /** 287: a seção «Etiqueta» da ficha da amostra recebida (RN-232). */
  etiquetaDe?(amostraId: string): ReactNode;
}) {
  const [resumo, setResumo] = useState<ResumoDaApi | null>(null);
  const [erro, setErro] = useState(false);
  const [sel, setSel] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setErro(false);
    api<ResumoDaApi>(`/os/${os.id}/resumo`)
      .then((r) => { if (vivo) setResumo(r); })
      .catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; };
  }, [os.id, recebidas, previstas]);

  const linhas = useMemo(() => (resumo
    ? montarLinhas(recebidas, previstas, resumo.ensaios, resumo.marcas)
    : []), [resumo, recebidas, previstas]);

  const metodos = useMemo(() => {
    if (!resumo) return [];
    /* O escopo vigente primeiro; o que a OS usa fora dele, depois. */
    return [...resumo.metodos].sort((a, b) =>
      Number(b.na_os) - Number(a.na_os)
      || (a.escopo_ordem ?? 999) - (b.escopo_ordem ?? 999)
      || a.codigo.localeCompare(b.codigo));
  }, [resumo]);

  const linhaSel = linhas.find((l) => l.chave === sel) ?? null;
  /* A ficha fecha com Esc. */
  useEffect(() => {
    if (!sel) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setSel(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [sel]);

  return (
    <div className="resumo-os">
      <FaixaDeSituacao os={os} />
      {aviso}
      <div className="resumo-colunas">
        <div className="resumo-principal">
          <EscopoEAndamento os={os} resumo={resumo} />
          <AmostrasEEnsaios os={os} linhas={linhas} metodos={metodos}
            carregando={!resumo && !erro} erro={erro}
            sel={sel} aoSelecionar={(c) => setSel((s) => (s === c ? null : c))} />
        </div>
        <aside className="resumo-lateral" aria-label="Instruções, recebimentos e dados da OS">
          {linhaSel ? (
            <FichaDaLinha l={linhaSel} metodos={metodos} aoFechar={() => setSel(null)}
              extra={linhaSel.amostra && etiquetaDe ? etiquetaDe(linhaSel.amostra.id) : null} />
          ) : (
            <>
              <InstrucoesDaOs linhas={linhas} metodos={metodos} />
              <RecebimentosDaOs os={os} previstas={previstas} recebidas={recebidas}
                aoPrevisao={aoPrevisao} aoMudar={aoMudar} />
              {dados}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ── A faixa de situação ──────────────────────────────────────────── */

/**
 * As três frentes e o prazo numa linha (decidido em 06/10: «a faixa de
 * situação pode substituir o cartão grande?» — «Sim»). A frase que
 * explicava por que são três sai da tela e vai para o ⓘ.
 */
export function FaixaDeSituacao({ os }: { os: Os }) {
  const prazo = prazoEmPalavras(os);
  const aprovados = Number(os.ensaios_aprovados ?? 0);
  const total = Number(os.ensaios_total ?? 0);
  const devolvidos = Number(os.ensaios_devolvidos ?? 0);
  /* 332 (D1): a marca em amostra por chegar é programação — a mesma conta da régua. */
  const { reservados } = numerosDaOs(os);
  const explica = 'As três situações andam separadas e se atualizam sozinhas conforme o '
    + 'serviço anda — ninguém as digita; cada mudança fica no Histórico, com quem a causou '
    + `e quando. Técnica: ${DIZ_STATUS.tecnico} Relatório: ${DIZ_STATUS.relatorio} `
    + `Medição: ${DIZ_STATUS.medicao}`;
  const frente = (campo: CampoStatus, valor: string, extra?: ReactNode) => (
    <div className={`fs-celula fs-${campo}`}>
      <span className="fs-rot">{TITULO_STATUS[campo]}</span>
      <span className="fs-valor">
        <Selo tom={TOM_POR_CAMPO[campo][valor] ?? 'neutro'}>{rotulo(valor)}</Selo>
        {extra}
      </span>
    </div>
  );
  return (
    <section className="faixa-situacao" aria-label="Situação da OS">
      {os.cancelada
        ? (
          <div className="fs-celula fs-tecnico">
            <span className="fs-rot">{TITULO_STATUS.tecnico}</span>
            <span className="fs-valor"><Selo tom="neutro">Cancelada</Selo></span>
          </div>
        )
        : os.suspensa
          ? (
            <div className="fs-celula fs-tecnico">
              <span className="fs-rot">{TITULO_STATUS.tecnico}</span>
              <span className="fs-valor"><Selo tom="atencao">Suspensa</Selo></span>
            </div>
          )
          : frente('tecnico', os.status_tecnico, (
            <span className="fs-conta">
              {total > 0
                ? `${aprovados} de ${total} ensaio(s) aprovado(s)`
                  + (reservados > 0 ? ` · ${reservados} por chegar` : '')
                : reservados > 0
                  ? `${plural(reservados, 'ensaio marcado', 'ensaios marcados')} nas amostras por chegar`
                  : 'nenhum ensaio programado'}
              {devolvidos > 0 && ` · ${devolvidos} devolvido(s)`}
            </span>
          ))}
      {frente('relatorio', os.status_relatorio)}
      {frente('medicao', os.status_medicao)}
      <div className="fs-celula fs-prazo">
        <span className="fs-rot">Prazo</span>
        <span className="fs-valor">
          {os.prazo_contratual
            ? (
              <>
                <span className="mono">{data(os.prazo_contratual)}</span>
                {prazo && (
                  <b className={os.atrasada ? 'prazo-atrasado' : undefined}>{' · '}{prazo}</b>
                )}
              </>
            )
            : <span className="nada">sem prazo</span>}
        </span>
        <button type="button" className="fs-info" aria-label="Sobre as situações da OS"
          title={explica}>i</button>
      </div>
    </section>
  );
}

/* ── Escopo e andamento ───────────────────────────────────────────── */

/**
 * O quadro 1 do PDF, vivo: para cada método do escopo, o contratado e a
 * barra aprovado / na bancada / programado / por chegar / saldo — com a
 * mesma conta da faixa do escopo (268) e o saldo por extenso.
 */
export function EscopoEAndamento({ os, resumo }: { os: Os; resumo: ResumoDaApi | null }) {
  const linhas = (os.escopo_por_metodo ?? []).map((x) => linhaDoServidor(x as EscopoDoServidor));
  const escopoDe = new Map((resumo?.metodos ?? []).map((m) => [m.codigo, m.escopo]));
  const contar = (codigo: string) => {
    const es = (resumo?.ensaios ?? []).filter((e) => e.codigo === codigo);
    return {
      ok: es.filter((e) => e.status === 'aprovado').length,
      bancada: es.filter((e) => NA_BANCADA.has(e.status)).length,
      prog: es.filter((e) => e.status === 'programado').length,
    };
  };
  const contratados = linhas.reduce((n, l) => n + l.contratada, 0);
  /* 332 (D1): programado inclui a marca por chegar, como na régua. */
  const programados = linhas.reduce((n, l) => n + l.programados + (l.reservados ?? 0), 0);
  const porChegarTotal = linhas.reduce((n, l) => n + (l.reservados ?? 0), 0);
  const aprovados = linhas.reduce((n, l) => n + contar(l.codigo).ok, 0);
  const fmt = (n: number) => n.toLocaleString('pt-BR');

  return (
    <section className="cartao escopo-andamento" aria-labelledby="t-escopo-andamento">
      <div className="cartao-cabeca">
        <h2 id="t-escopo-andamento">Escopo e andamento</h2>
        {linhas.length > 0 && (
          <span className="sub">
            {fmt(contratados)} contratado(s) · {fmt(programados)} programado(s)
            {porChegarTotal > 0 && ` · ${fmt(porChegarTotal)} por chegar`} · {fmt(aprovados)} aprovado(s)
          </span>
        )}
        <Link className="cabeca-link" to={rotaDaAba(os.id, 'escopo')}>Ver o escopo ›</Link>
      </div>
      {linhas.length === 0 ? (
        <Vazio>
          Esta OS ainda não tem ensaio no escopo — é o escopo que libera a
          programação.
        </Vazio>
      ) : (
        <>
          <div className="ea-tabela" role="table" aria-label="Escopo e andamento por ensaio">
            <div className="ea-linha ea-cab" role="row">
              <span role="columnheader">Ensaio</span>
              <span role="columnheader" className="num">Contratado</span>
              <span role="columnheader">Andamento</span>
              <span role="columnheader">Situação</span>
            </div>
            {linhas.map((l) => {
              const c = contar(l.codigo);
              const porChegar = l.reservados ?? 0;
              const usados = l.programados + porChegar;
              const total = Math.max(l.contratada, usados, 1);
              const pc = (n: number) => `${(100 * n) / total}%`;
              const s = saldoDa(l);
              const partes = [
                c.ok ? `${c.ok} ✓` : null,
                c.bancada ? `${c.bancada} ▶` : null,
                c.prog ? `${c.prog} ◷` : null,
                porChegar ? `${porChegar} ○` : null,
                s.palavra,
              ].filter(Boolean);
              return (
                <div key={l.id} className="ea-linha" role="row" data-metodo={l.codigo}>
                  <span role="cell" className="ea-ensaio">
                    <span className={`mini escopo-${escopoDe.get(l.codigo) ?? 'outros'}`}>
                      {l.codigo}
                    </span>
                    <span className="ea-nome">
                      {l.nome}
                      {l.cps && l.cps > 1 ? ` · ${l.cps} CPs` : ''}
                    </span>
                  </span>
                  <span role="cell" className="num mono">{fmt(l.contratada)}</span>
                  <span role="cell" className="ea-barra"
                    aria-label={`${c.ok} aprovado(s), ${c.bancada} na bancada, ${c.prog} programado(s), `
                      + `${porChegar} por chegar, de ${fmt(l.contratada)} contratado(s)`}>
                    <span className="trecho aprovado" style={{ width: pc(c.ok) }} />
                    <span className="trecho bancada" style={{ width: pc(c.bancada) }} />
                    <span className="trecho programado" style={{ width: pc(c.prog) }} />
                    <span className="trecho por-chegar" style={{ width: pc(porChegar) }} />
                  </span>
                  <span role="cell" className={`ea-texto ${s.tom}`}>{partes.join(' · ')}</span>
                </div>
              );
            })}
          </div>
          <p className="ea-legenda" aria-hidden="true">
            <span><i className="trecho aprovado" />✓ aprovado</span>
            <span><i className="trecho bancada" />▶ ? ! na bancada</span>
            <span><i className="trecho programado" />◷ programado</span>
            <span><i className="trecho por-chegar" />○ marcado em amostra por chegar</span>
            <span><i className="trecho saldo" />saldo</span>
          </p>
        </>
      )}
    </section>
  );
}

/* ── Amostras e ensaios ───────────────────────────────────────────── */

function AmostrasEEnsaios({
  os, linhas, metodos, carregando, erro, sel, aoSelecionar,
}: {
  os: Os;
  linhas: LinhaDoResumo[];
  metodos: MetodoDoResumo[];
  carregando: boolean;
  erro: boolean;
  sel: string | null;
  aoSelecionar(chave: string): void;
}) {
  const [recorte, setRecorte] = useState<Recorte>('todas');
  const [busca, setBusca] = useState('');
  const [porFuro, setPorFuro] = useState(true);

  const q = semAcento(busca.trim());
  const visiveis = linhas
    .filter(FILTRO[recorte])
    .filter((l) => !q || [l.titulo, l.ident, l.furo, l.prevista?.identificacao_prevista]
      .some((v) => v && semAcento(v).includes(q)));
  const ordenadas = porFuro
    ? [...visiveis].sort((x, y) => ordemDoFuro(x.furo, y.furo))
    : visiveis;
  const contaDoFuro = new Map<string, number>();
  for (const l of linhas) contaDoFuro.set(l.furo, (contaDoFuro.get(l.furo) ?? 0) + 1);
  const abreFuro = (i: number) => porFuro
    && (i === 0 || ordenadas[i - 1].furo !== ordenadas[i].furo);

  const recebidas = linhas.filter((l) => l.tipo === 'recebida').length;
  const porChegar = linhas.length - recebidas;

  return (
    <section className="cartao amostras-ensaios" aria-labelledby="t-amostras-ensaios">
      <div className="cartao-cabeca">
        <h2 id="t-amostras-ensaios">Amostras e ensaios</h2>
        <span className="sub">
          {linhas.length} amostra(s) · {recebidas} recebida(s) · {porChegar} por chegar
        </span>
        <Link className="cabeca-link" to={rotaDaAba(os.id, 'programacao')}>Programar ›</Link>
      </div>

      <div className="ae-barra">
        <div className="ae-recortes" role="group" aria-label="Recorte das amostras">
          {(Object.keys(FILTRO) as Recorte[]).map((r) => (
            <button key={r} type="button" aria-pressed={recorte === r}
              className={recorte === r ? 'sel' : undefined}
              onClick={() => setRecorte(r)}>
              {NOME_DO_RECORTE[r]}{' '}
              <span className="aba-os-cont">{linhas.filter(FILTRO[r]).length}</span>
            </button>
          ))}
        </div>
        <div className="ae-ferramentas">
          <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar amostra, furo…" aria-label="Buscar amostra ou furo" />
          <label className="ae-agrupar">
            <input type="checkbox" checked={porFuro} onChange={(e) => setPorFuro(e.target.checked)} />
            Agrupar por furo
          </label>
        </div>
      </div>

      {carregando ? (
        <p className="sub ae-vazio">Carregando os ensaios…</p>
      ) : erro ? (
        <p className="sub ae-vazio">Não foi possível carregar os ensaios desta OS.</p>
      ) : linhas.length === 0 ? (
        <Vazio>
          Nenhuma amostra ainda. Elas aparecem aqui quando forem previstas na
          Programação ou recebidas.
        </Vazio>
      ) : ordenadas.length === 0 ? (
        <p className="sub ae-vazio">Nenhuma amostra neste recorte{busca ? ` com «${busca}»` : ''}.</p>
      ) : (
        <>
          {/* No computador, a grade; no celular, os cartões (a folha esconde um dos dois). */}
          <div className="rola grade-resumo-rola">
            <table className="grade-resumo">
              <thead>
                <tr>
                  <th scope="col" className="col-amostra">Amostra</th>
                  <th scope="col" className="num">Prof. (m)</th>
                  {metodos.map((m) => (
                    <th key={m.id} scope="col" className={`met escopo-${m.escopo ?? 'outros'}`}
                      title={`${m.nome}${m.na_os ? '' : ' — fora do escopo vigente'}`}>
                      <span className="mono">{m.codigo}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ordenadas.map((l, i) => (
                  <LinhaDaGrade key={l.chave} l={l} metodos={metodos}
                    abreFuro={abreFuro(i)} contaDoFuro={contaDoFuro.get(l.furo) ?? 0}
                    sel={sel === l.chave} aoSelecionar={aoSelecionar} />
                ))}
              </tbody>
            </table>
          </div>
          <ul className="cartoes-resumo" aria-label="Amostras e ensaios">
            {ordenadas.map((l, i) => (
              <li key={l.chave}>
                {abreFuro(i) && (
                  <div className="cartao-furo">
                    <b className="mono">{l.furo || 'Sem furo'}</b>{' '}
                    <span className="sub">· {contaDoFuro.get(l.furo) ?? 0} amostra(s)</span>
                  </div>
                )}
                <button type="button" className={`cartao-amostra${sel === l.chave ? ' sel' : ''}`}
                  aria-expanded={sel === l.chave} onClick={() => aoSelecionar(l.chave)}>
                  <span className="ca-codigo">
                    <NomeDaLinha l={l} emLinha />
                  </span>
                  <span className="ca-sub">{subDaLinha(l)}</span>
                  <span className="ca-pilulas">
                    {metodos.map((m) => {
                      const c = celulaDe(l, m.codigo);
                      return c.classe === 'nada' ? null : (
                        <span key={m.id} className={`pilula-ensaio ${c.classe}`} title={c.titulo}>
                          <span aria-hidden="true">{c.simbolo}</span> {m.codigo}
                          {c.instrucao && <span className="tem-instrucao" aria-label="tem instrução" />}
                        </span>
                      );
                    })}
                    {!temEnsaio(l) && (
                      <span className="nada">
                        {l.tipo === 'por_chegar' ? 'nenhum ensaio marcado' : 'nenhum ensaio'}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="ae-legenda">
            {(['ok', 'andamento', 'atencao', 'critico', 'neutro', 'por-chegar', 'nada'] as const).map((k) => (
              <span key={k}><span className={`celula ${k}`} aria-hidden="true">{LEGENDA[k][0]}</span>{LEGENDA[k][1]}</span>
            ))}
            <span><span className="celula nada" aria-hidden="true"><span className="tem-instrucao" /></span>tem instrução</span>
          </p>
        </>
      )}
    </section>
  );
}

const LEGENDA: Record<string, [string, string]> = {
  ok: ['✓', 'Aprovado'],
  andamento: ['▶', 'Em execução'],
  atencao: ['?', 'Aguardando verificação / pausado'],
  critico: ['!', 'Devolvido'],
  neutro: ['◷', 'Programado'],
  'por-chegar': ['○', 'Marcado, por chegar'],
  nada: ['·', 'Não pedido'],
};

/*
 * 303 (RN-248): a linha é chamada pelo nome que o cliente deu, com o código
 * AM logo depois. `titulo` continua sendo o código na recebida — é por ele
 * que a lista desempata a ordem —; o que muda é só o que se mostra.
 */
const nomeDaLinha = (l: LinhaDoResumo): AmostraComNome => (l.tipo === 'recebida'
  ? { codigo: l.titulo, identificacao: l.ident }
  : { identificacao: l.titulo });

/** O nome na tela; a prevista ainda não tem código — ele nasce na chegada. */
function NomeDaLinha({ l, emLinha, forte }: {
  l: LinhaDoResumo; emLinha?: boolean; forte?: boolean;
}) {
  if (l.tipo === 'recebida') {
    return <NomeDaAmostra amostra={nomeDaLinha(l)} emLinha={emLinha} forte={forte} />;
  }
  return (
    <NomeDaAmostra amostra={{ identificacao: `○ ${l.titulo}` }} emLinha={emLinha} forte={forte}
      depois={<span className="nome-amostra-codigo">código AM ao chegar</span>} />
  );
}

const subDaLinha = (l: LinhaDoResumo) => [
  /* A identificação do cliente já é o nome (303); aqui fica o resto. */
  l.tipo === 'por_chegar' ? 'por chegar' : null,
  l.prof ? `${l.prof} m` : null,
  l.tipoAmostra ? rotulo(l.tipoAmostra) : null,
  l.tipo === 'por_chegar' && l.prevista?.previsao_chegada
    ? `previsão ${data(l.prevista.previsao_chegada)}` : null,
].filter(Boolean).join(' · ');

function LinhaDaGrade({ l, metodos, abreFuro, contaDoFuro, sel, aoSelecionar }: {
  l: LinhaDoResumo; metodos: MetodoDoResumo[]; abreFuro: boolean; contaDoFuro: number;
  sel: boolean; aoSelecionar(chave: string): void;
}) {
  return (
    <>
      {abreFuro && (
        <tr className="furo-linha">
          <th scope="rowgroup" colSpan={2 + metodos.length}>
            <span className="mono">{l.furo || 'Sem furo'}</span>{' '}
            <span className="sub">· {contaDoFuro} amostra(s)</span>
          </th>
        </tr>
      )}
      <tr className={`${l.tipo === 'por_chegar' ? 'por-chegar' : ''}${sel ? ' sel' : ''}`}
        data-chave={l.chave}>
        <th scope="row" className="col-amostra">
          <button type="button" className="abrir-linha" aria-expanded={sel}
            onClick={() => aoSelecionar(l.chave)}>
            <NomeDaLinha l={l} />
          </button>
          <span className="ident-amostra">
            {l.tipo === 'por_chegar'
              ? ['por chegar', l.tipoAmostra ? rotulo(l.tipoAmostra) : null].filter(Boolean).join(' · ')
              : [l.tipoAmostra ? rotulo(l.tipoAmostra) : null].filter(Boolean).join(' · ')}
          </span>
        </th>
        <td className="num mono">{l.prof || '—'}</td>
        {metodos.map((m) => {
          const c = celulaDe(l, m.codigo);
          return (
            <td key={m.id} className="cel-resumo">
              <span className={`celula ${c.classe}`} title={c.titulo}>
                <span aria-hidden="true">{c.simbolo}</span>
                <span className="so-leitor">{c.titulo}</span>
                {c.instrucao && <span className="tem-instrucao" aria-label="tem instrução" />}
              </span>
            </td>
          );
        })}
      </tr>
    </>
  );
}

/* ── A ficha da amostra ───────────────────────────────────────────── */

const agendaDe = (e: EnsaioDoResumo) => {
  const onde = e.equipamento_codigo ?? e.frente_nome;
  const ini = e.inicio_real ?? e.inicio_previsto;
  const quando = ini
    ? (e.fim_previsto && e.fim_previsto.slice(0, 10) !== ini.slice(0, 10)
      ? `${data(ini)} → ${data(e.fim_previsto)}` : data(ini))
    : 'sem data no cronograma';
  return [onde, quando, e.executado_por_nome].filter(Boolean).join(' · ');
};

function FichaDaLinha({ l, metodos, aoFechar, extra }: {
  l: LinhaDoResumo; metodos: MetodoDoResumo[]; aoFechar(): void;
  /** 287: a «Etiqueta» da amostra recebida. */
  extra?: ReactNode;
}) {
  const escopoDe = new Map(metodos.map((m) => [m.codigo, m.escopo]));
  const a = l.amostra;
  const p = l.prevista;
  const dl = (rot: string, v: ReactNode) => (
    <div className="campo-ficha"><dt>{rot}</dt><dd>{v ?? <span className="nada">—</span>}</dd></div>
  );
  return (
    <section className="ficha-resumo" aria-label="Ficha da amostra">
      <header>
        <div>
          <p className="rot">{l.tipo === 'por_chegar' ? 'Amostra por chegar' : 'Amostra'}</p>
          <h3><NomeDaLinha l={l} forte={false} /></h3>
          <p className="sub">{subDaLinha(l)}</p>
        </div>
        <button type="button" className="fechar-ficha" aria-label="Fechar a ficha" onClick={aoFechar}>×</button>
      </header>
      {a ? (
        <>
          <div>
            <Selo tom={TOM_DA_AMOSTRA[a.status] ?? 'neutro'}>{rotulo(a.status)}</Selo>
          </div>
          <dl>
            {dl('Recebimento', a.lote_codigo
              ? <><span className="mono">{a.lote_codigo}</span>{a.datahora_recebimento ? ` · ${dataHora(a.datahora_recebimento)}` : ''}</>
              : null)}
            {dl('Guardada em', a.local_nome ?? a.onde_esta ?? null)}
          </dl>
          {extra}
        </>
      ) : p ? (
        <dl>
          {dl('Previsão de chegada', p.previsao_chegada
            ? `${data(p.previsao_chegada)}${p.previsao_origem ? ` (${p.previsao_origem === 'cliente' ? 'cliente' : 'Testesolo'})` : ''}`
            : 'não informada')}
        </dl>
      ) : null}

      <p className="rot ficha-resumo-rot">Ensaios desta amostra</p>
      {l.tipo === 'recebida' && l.ensaios.length === 0 && (
        <p className="sub">Nenhum ensaio nesta amostra.</p>
      )}
      {l.tipo === 'por_chegar' && l.marcas.length === 0 && (
        <p className="sub">Nenhum ensaio marcado nesta amostra.</p>
      )}
      <ul className="ficha-resumo-ensaios">
        {l.ensaios.map((e) => (
          <li key={e.id}>
            <div className="fre-topo">
              <span className={`mini escopo-${e.escopo ?? 'outros'}`}>{e.codigo}</span>
              <SeloDoEnsaio status={e.status} />
            </div>
            <div className="fre-agenda">{agendaDe(e)}{e.atrasado ? ' · atrasado' : ''}</div>
            {e.status === 'devolvido_para_correcao' && e.motivo_devolucao && (
              <div className="fre-devolucao">Devolvido: «{e.motivo_devolucao}»</div>
            )}
            {(e.instrucao ?? '').trim() && (
              <div className="fre-instrucao"><b>Instrução:</b> {e.instrucao}</div>
            )}
            <Link to={`/ensaio/${e.id}/situacao`}>Abrir o ensaio ›</Link>
          </li>
        ))}
        {l.marcas.map((m) => (
          <li key={m.codigo}>
            <div className="fre-topo">
              <span className={`mini escopo-${escopoDe.get(m.codigo) ?? 'outros'}`}>{m.codigo}</span>
              <span className="selo neutro">○ Marcado</span>
            </div>
            <div className="fre-agenda">nasce programado quando a amostra chegar</div>
            {(m.instrucao ?? '').trim() && (
              <div className="fre-instrucao"><b>Instrução:</b> {m.instrucao}</div>
            )}
          </li>
        ))}
      </ul>
      {a && (
        <div className="acoes-ficha">
          <Link className="botao" to={`/amostra/${encodeURIComponent(a.codigo)}`}>Abrir a amostra</Link>
        </div>
      )}
    </section>
  );
}

/* ── As instruções de execução ────────────────────────────────────── */

/**
 * O quadro 3 do PDF, em destaque: o mesmo texto para o mesmo método vira
 * uma instrução só, com as amostras a que se aplica.
 */
export function InstrucoesDaOs({ linhas, metodos }: {
  linhas: LinhaDoResumo[];
  /** Para pintar também as marcas (ainda sem ensaio) com a cor do escopo. */
  metodos?: MetodoDoResumo[];
}) {
  const escopoDe = new Map((metodos ?? []).map((m) => [m.codigo, m.escopo] as const));
  type Grupo = { codigo: string; texto: string; escopo: string | null; alvos: string[] };
  const grupos = new Map<string, Grupo>();
  const juntar = (codigo: string, escopo: string | null, texto: string | null, alvo: string) => {
    const t = (texto ?? '').trim();
    if (!t) return;
    const chave = `${codigo} ${t}`;
    const g = grupos.get(chave) ?? { codigo, texto: t, escopo, alvos: [] };
    if (!g.alvos.includes(alvo)) g.alvos.push(alvo);
    grupos.set(chave, g);
  };
  for (const l of linhas) {
    // 303: a instrução diz a que amostra vale pelo nome do cliente, com o
    // código junto (o código nunca some).
    const alvo = nomeDaAmostra(nomeDaLinha(l));
    for (const e of l.ensaios) juntar(e.codigo, e.escopo, e.instrucao, alvo);
    for (const m of l.marcas) juntar(m.codigo, escopoDe.get(m.codigo) ?? null, m.instrucao, alvo);
  }
  const lista = [...grupos.values()];
  if (!lista.length) return null;
  return (
    <section className="cartao instrucoes-os" aria-labelledby="t-instrucoes-os">
      <div className="cartao-cabeca">
        <h2 id="t-instrucoes-os">Instruções de execução</h2>
        <span className="aba-os-cont">{lista.length}</span>
      </div>
      <ul>
        {lista.map((g) => (
          <li key={`${g.codigo} ${g.texto}`}>
            <div className="io-alvo">
              <span className={`mini escopo-${g.escopo ?? 'outros'}`}>{g.codigo}</span>
              <span className="sub">{g.alvos.join(', ')}</span>
            </div>
            <p>{g.texto}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Os recebimentos ──────────────────────────────────────────────── */

/**
 * Os lotes que chegaram para esta OS (pedido de 06/10: «um resumo de todos
 * os lotes de amostras que chegaram vinculados àquela OS») e o que ainda
 * falta chegar.
 */
export function RecebimentosDaOs({ os, previstas, recebidas, aoPrevisao, aoMudar }: {
  os: Os;
  previstas: AmostraPrevista[];
  recebidas: AmostraDoResumo[];
  aoPrevisao?: () => void;
  aoMudar?: () => void | Promise<void>;
}) {
  const { pode } = useSessao();
  const [lotes, setLotes] = useState<LoteDoResumo[] | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);

  useEffect(() => {
    let vivo = true;
    api<LoteDoResumo[]>(`/recebimento?os=${encodeURIComponent(os.id)}`)
      .then((r) => { if (vivo) setLotes(r); })
      .catch(() => { if (vivo) { setSemAcesso(true); setLotes([]); } });
    return () => { vivo = false; };
  }, [os.id, recebidas]);

  const faltam = previstas.filter((p) => !p.atendida);
  const previsoes = faltam.map((p) => p.previsao_chegada).filter((x): x is string => !!x).sort();
  const podeReceber = pode('amostra.receber') && !os.cancelada && !os.suspensa && !os.arquivada;

  /* Os furos de cada lote, a partir das amostras da OS: «SP-01 (4), SP-02 (2)». */
  const furosDo = (loteId: string) => {
    const c = new Map<string, number>();
    for (const a of recebidas) {
      if (a.lote_recebimento_id !== loteId) continue;
      const f = a.ponto_investigacao?.trim() || 'sem furo';
      c.set(f, (c.get(f) ?? 0) + 1);
    }
    return [...c.entries()].sort((x, y) => ordemDoFuro(x[0], y[0]))
      .map(([f, n]) => `${f} (${n})`).join(', ');
  };

  return (
    <section className="cartao recebimentos-os" aria-labelledby="t-recebimentos-os">
      <div className="cartao-cabeca">
        <h2 id="t-recebimentos-os">Recebimentos</h2>
        {podeReceber && (
          <Link className="botao secundario" to={`/recebimento/novo?os=${os.id}`}>
            Receber amostras
          </Link>
        )}
      </div>
      <div className="ro-numeros">
        <div><b>{lotes ? lotes.length : '…'}</b><span>lote(s)</span></div>
        <div><b>{recebidas.length}</b><span>recebida(s)</span></div>
        <div><b>{faltam.length}</b><span>por chegar</span></div>
      </div>
      {/* 281: o lote do mesmo cliente que chegou sem OS — vincula daqui. */}
      <AvisoSemOs os={os} previstas={previstas} aoVincular={aoMudar} />
      {semAcesso ? (
        <p className="sub ro-vazio">Seu perfil não vê os recebimentos.</p>
      ) : lotes && lotes.length === 0 ? (
        <p className="sub ro-vazio">Nenhum recebimento para esta OS ainda.</p>
      ) : (
        <ul className="ro-lotes">
          {(lotes ?? []).map((l) => {
            const furos = furosDo(l.id);
            return (
              <li key={l.id} data-lote={l.codigo}>
                <div className="ro-topo">
                  <Link className="mono" to={`/recebimento/${l.id}`}>{l.codigo}</Link>
                  <span>
                    {l.status !== 'concluido' && <Selo tom="atencao">Rascunho</Selo>}
                    <Selo tom={TOM_DA_DECISAO[l.decisao] ?? 'neutro'}>{rotulo(l.decisao)}</Selo>
                  </span>
                </div>
                <div className="ro-linha">
                  <span className="mono">{dataHora(l.datahora_recebimento)}</span>
                  {' · '}{Number(l.amostras)} amostra(s){furos ? ` · ${furos}` : ''}
                </div>
                <div className="sub">
                  {[
                    l.responsavel_entrega_nome
                      ? `Entregou ${l.responsavel_entrega_nome}${l.responsavel_entrega_empresa ? ` (${l.responsavel_entrega_empresa})` : ''}`
                      : null,
                    l.responsavel_recebimento ? `recebeu ${l.responsavel_recebimento}` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
                {l.decisao !== 'aceite' && (l.ressalvas ?? '').trim() && (
                  <div className="ro-ressalva">Ressalva: «{l.ressalvas}»</div>
                )}
                <div className="ro-pe">
                  {l.status !== 'concluido'
                    ? <span className="sub">em recebimento</span>
                    : l.aprovado
                      ? (
                        <span className="ok">
                          ✓ {l.aprovacao_dispensada && !l.aprovado_por_nome
                            ? 'aprovação dispensada'
                            : `aprovado${l.aprovado_por_nome ? ` por ${l.aprovado_por_nome}` : ''}${l.aprovado_em ? ` em ${data(l.aprovado_em)}` : ''}`}
                        </span>
                      )
                      : <span className="atencao">▲ aguarda aprovação</span>}
                  {l.pdf_arquivo_id && Number(l.pdf_revisao) > 0 && (
                    <Link to={`/recebimento/${l.id}?aba=documento`}>
                      PDF R{String(l.pdf_revisao).padStart(2, '0')}
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {faltam.length > 0 && (
        <div className="ro-por-chegar">
          <b>○ {faltam.length} amostra(s) por chegar</b>
          <span className="sub">
            {faltam.slice(0, 3).map((p) => p.identificacao_prevista ?? '—').join(', ')}
            {faltam.length > 3 ? ` e mais ${faltam.length - 3}` : ''}
            {previsoes.length ? ` · previsão ${data(previsoes[0])}` : ' · sem previsão de chegada'}
          </span>
          {aoPrevisao && pode('os.programar') && !os.cancelada && (
            <button type="button" className="botao discreto" onClick={aoPrevisao}>
              Previsão de chegada
            </button>
          )}
        </div>
      )}
    </section>
  );
}
